/**
 * In-app notifications backing the notification bell.
 *
 * Mirrors `public.user_notifications` (see dialup/db_functions/user_notifications.sql).
 * Rows are written by database triggers only — the client reads, marks read and
 * deletes, never inserts.
 */

/**
 * One notification per finished call. Callbacks, bookings and first-time
 * callers are folded into the payload as flags rather than emitted separately:
 * all three are created by agent tools *during* the call, so they are already
 * committed when the call ends. One event, one notification.
 */
export type NotificationType = 'conversation_completed';

/** What happened to the booking during the call this notification is about. */
export type NotificationBookingChange = 'created' | 'cancelled' | 'rescheduled';

export interface NotificationBooking {
  id: number;
  start_time: string;
  end_time: string;
  service_name: string | null;
  /**
   * Absent on notifications written before this field existed; those were all
   * newly created bookings, so that is the safe fallback.
   */
  change?: NotificationBookingChange;
}

/** Self-contained render data — the bell never joins anything to draw a row. */
export interface ConversationCompletedPayload {
  caller_id: string | null;
  /** Null when the caller has no saved name; fall back to caller_id. */
  contact_name: string | null;
  agent_name: string | null;
  /** Call length in seconds. */
  duration: number | null;
  /** First call this number has ever made to this agent. */
  is_new_contact: boolean;
  callback_requested: boolean;
  /** Short reason derived by the agent from the live conversation. */
  callback_reason: string | null;
  booking: NotificationBooking | null;
}

export interface AppNotification {
  id: number;
  user_id: string;
  agent_id: number | null;
  type: NotificationType;
  entity_type: string | null;
  entity_id: string | null;
  payload: ConversationCompletedPayload;
  created_at: string;
  read_at: string | null;
}

/**
 * A notification demands attention when the caller asked for a human. Used to
 * pick the accent colour and the title, and to decide toast urgency.
 */
export const isActionRequired = (notification: AppNotification): boolean =>
  notification.payload?.callback_requested === true;
